import type { ApiReturnResource } from '@highimpact-frontend/api-types';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useDashboardHost } from '../context/DashboardHostContext';
import { dashboardQueryKeys } from './queryKeys';
import { normalizeResourceIds } from './resourceQueryUtils';

export interface UseResourcesQueryResult {
  readonly resources: readonly ApiReturnResource[];
  readonly loading: boolean;
  readonly error: Error | null;
  readonly reload: () => Promise<void>;
}

/**
 * Loads image/text resources by id through TanStack Query + {@link ResourceApi.get}.
 */
export function useResourcesQuery(
  resourceIds: readonly number[]
): UseResourcesQueryResult {
  const host = useDashboardHost();
  const normalizedIds = useMemo(
    () => normalizeResourceIds(resourceIds),
    [resourceIds]
  );

  const query = useQuery({
    queryKey: dashboardQueryKeys.resources(normalizedIds),
    queryFn: () => host.api.resource.get(normalizedIds),
    enabled: normalizedIds.length > 0,
    staleTime: 60_000,
  });

  return {
    resources: query.data ?? [],
    loading: normalizedIds.length > 0 && query.isPending,
    error:
      query.error instanceof Error
        ? query.error
        : query.error
          ? new Error(String(query.error))
          : null,
    reload: async () => {
      await query.refetch();
    },
  };
}

export type ResourcesQueryData = ApiReturnResource[];
